import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  getUserWorkflows,
  getUserBatches,
  getAssignedRequests,
} from '@/services/qc/qcTaskAssignmentsService';
import { getAllTagsForWorkflow } from '@/services/qc/workflowTagsService';
import { useAppSelector } from '@/store/hooks';
import { parseEnvNumberList } from '../../../shared/utils/envParse';

export const ANNOT_SEG_TABS = {
  NEW: 'NEW',
  SAVE_FOR_LATER: 'SAVE_FOR_LATER',
  SUBMITTED: 'SUBMITTED',
};

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const FALLBACK_DEFAULT_PAGE_SIZE = 20;
const WORKFLOWS_STALE_MS = 5 * 60 * 1000;
const BATCHES_STALE_MS = 5 * 60 * 1000;
const ASSIGNMENTS_STALE_MS = 30 * 1000;

const FILTER_PARAM_KEYS = ['language', 'district', 'state', 'duration', 'file_name', 'search'];

function reqStatusFromTab(tab) {
  if (tab === ANNOT_SEG_TABS.SAVE_FOR_LATER) return 'IN-PROGRESS';
  if (tab === ANNOT_SEG_TABS.SUBMITTED) return 'SUBMITTED';
  return 'OPEN';
}

function normalizeTab(raw) {
  const t = String(raw ?? ANNOT_SEG_TABS.NEW).toUpperCase();
  return Object.values(ANNOT_SEG_TABS).includes(t) ? t : ANNOT_SEG_TABS.NEW;
}

function buildFiltersFromSearchParams(searchParams) {
  return {
    language: searchParams.get('language') ?? '',
    district: searchParams.get('district') ?? '',
    state: searchParams.get('state') ?? '',
    duration: searchParams.get('duration') ?? '',
    file_name: searchParams.get('file_name') ?? '',
    search: searchParams.get('search') ?? '',
  };
}

function readDefaultPageSize() {
  const n = Number(import.meta.env.VITE_QC_TASK_DEFAULT_PAGE_SIZE);
  return Number.isFinite(n) && n > 0 ? n : FALLBACK_DEFAULT_PAGE_SIZE;
}

export function useAnnotSegPageController() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(() => normalizeTab(searchParams.get('tab')));
  const [workflowId, setWorkflowId] = useState(() => searchParams.get('workflowId') ?? '');
  const [batchId, setBatchId] = useState(() => searchParams.get('batchId') ?? '');
  const [pagination, setPagination] = useState(() => ({
    pageIndex: 0,
    pageSize: readDefaultPageSize(),
  }));
  const [sorting, setSorting] = useState([]);

  const sortBy = sorting?.[0]?.id ?? 'file_name';
  const sortDirection = sorting?.[0]?.desc ? 'DESC' : 'ASC';

  const pageSizeOptions = useMemo(
    () => parseEnvNumberList(import.meta.env.VITE_QC_TASK_PAGE_SIZES, DEFAULT_PAGE_SIZE_OPTIONS),
    [],
  );
  const defaultPageSize = useMemo(() => readDefaultPageSize(), []);
  const activeReqStatus = useMemo(() => reqStatusFromTab(activeTab), [activeTab]);
  const tableFilters = useMemo(() => buildFiltersFromSearchParams(searchParams), [searchParams]);

  const applyTableFiltersToUrl = useCallback(
    (nextFilters) => {
      const next = new URLSearchParams(searchParams);
      FILTER_PARAM_KEYS.forEach((k) => next.delete(k));
      Object.entries(nextFilters ?? {}).forEach(([k, v]) => {
        if (!FILTER_PARAM_KEYS.includes(k)) return;
        const s = v == null ? '' : String(v).trim();
        if (s) next.set(k, s);
      });
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const {
    data: workflowsApiData = [],
    isLoading: isWorkflowLoading,
    isFetching: isWorkflowFetching,
    error: workflowsError,
  } = useQuery({
    queryKey: ['annotSegWorkflows', activeReqStatus],
    queryFn: () => getUserWorkflows(activeReqStatus),
    enabled: Boolean(activeReqStatus),
    staleTime: WORKFLOWS_STALE_MS,
    gcTime: WORKFLOWS_STALE_MS * 2,
  });

  // getUserWorkflows only returns { workflow_id, total_batch_count }.
  // Join with Redux globalWorkflows (loaded at login by WorkflowTagsProvider) to get
  // workflow_type and name, then filter to SEGMENTATION / ANNOTATION only.
  const globalWorkflows = useAppSelector((state) => state.workflowTags.globalWorkflows);

  const workflowOptions = useMemo(() => {
    const ids = Array.isArray(workflowsApiData) ? workflowsApiData : [];
    return ids
      .map((apiRow) => {
        const full = Array.isArray(globalWorkflows)
          ? globalWorkflows.find((g) => g.workflow_id === apiRow.workflow_id)
          : null;
        return full ? { ...apiRow, ...full } : apiRow;
      })
      .filter(
        (w) => w.workflow_type === 'SEGMENTATION' || w.workflow_type === 'ANNOTATION',
      );
  }, [workflowsApiData, globalWorkflows]);

  const {
    data: batchesApiData = [],
    isLoading: isBatchesLoading,
    isFetching: isBatchesFetching,
    isSuccess: isBatchesSuccess,
    error: batchesError,
  } = useQuery({
    queryKey: ['annotSegBatches', workflowId, activeReqStatus],
    queryFn: () => getUserBatches({ workflowId, reqStatus: activeReqStatus }),
    enabled: Boolean(workflowId && activeReqStatus),
    staleTime: BATCHES_STALE_MS,
    gcTime: BATCHES_STALE_MS * 2,
  });

  const batchOptions = useMemo(() => {
    if (!workflowId) return [];
    return Array.isArray(batchesApiData) ? batchesApiData : [];
  }, [batchesApiData, workflowId]);

  const tagsByWorkflowId = useAppSelector((state) => state.workflowTags.tagsByWorkflowId);

  const { data: apiTagData = [] } = useQuery({
    queryKey: ['annotSegTagsForWorkflow', workflowId],
    queryFn: () => getAllTagsForWorkflow(workflowId),
    enabled: Boolean(workflowId),
    staleTime: 5 * 60 * 1000,
  });

  const tagOptions = useMemo(() => {
    const fromRedux = (() => {
      if (!workflowId) return [];
      const rows = tagsByWorkflowId[workflowId];
      return Array.isArray(rows) ? rows.map((r) => r.tag_name).filter(Boolean) : [];
    })();
    const fromApi = Array.isArray(apiTagData)
      ? apiTagData.map((r) => r.tag_name).filter(Boolean)
      : [];
    return [...new Set([...fromRedux, ...fromApi])];
  }, [tagsByWorkflowId, workflowId, apiTagData]);

  const {
    data: assignmentsPayload,
    isLoading: isAssignmentsLoading,
    isFetching: isAssignmentsFetching,
    refetch: refetchAssignments,
    error: assignmentsError,
  } = useQuery({
    queryKey: [
      'annotSegTasks',
      activeReqStatus, workflowId, batchId,
      pagination.pageIndex, pagination.pageSize,
      tableFilters, sortBy, sortDirection,
    ],
    queryFn: () =>
      getAssignedRequests({
        reqStatus: activeReqStatus,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        batchId,
        workflowId,
        filters: tableFilters,
        sortBy,
        sortDirection,
      }),
    enabled: Boolean(activeReqStatus && workflowId && batchId),
    staleTime: ASSIGNMENTS_STALE_MS,
    gcTime: ASSIGNMENTS_STALE_MS * 2,
  });

  const handleSortingChange = useCallback(
    (updater) => {
      setSorting((prev) => (typeof updater === 'function' ? updater(prev) : updater));
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    },
    [],
  );

  const urlQueryKey = searchParams.toString();
  useEffect(() => {
    const params = new URLSearchParams(urlQueryKey);
    const t = normalizeTab(params.get('tab'));
    const w = params.get('workflowId') ?? '';
    const b = params.get('batchId') ?? '';
    setActiveTab((cur) => (cur === t ? cur : t));
    setWorkflowId((cur) => (cur === w ? cur : w));
    setBatchId((cur) => (cur === b ? cur : b));
  }, [urlQueryKey]);

  useEffect(() => {
    if (workflowOptions.length > 0 && !workflowId) {
      setWorkflowId(workflowOptions[0]?.workflow_id ?? '');
    }
  }, [workflowOptions, workflowId]);

  useEffect(() => {
    if (isBatchesSuccess && batchOptions.length > 0 && !batchId) {
      setBatchId(batchOptions[0]?.batch_id ?? '');
    }
  }, [batchOptions, isBatchesSuccess, batchId]);

  useEffect(() => {
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
  }, [activeTab, workflowId, batchId]);

  useEffect(() => {
    const next = new URLSearchParams();
    next.set('tab', activeTab);
    if (workflowId) next.set('workflowId', workflowId);
    if (batchId) next.set('batchId', batchId);
    Object.entries(tableFilters).forEach(([key, value]) => {
      if (value) next.set(key, value);
    });
    if (searchParams.toString() !== next.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [activeTab, workflowId, batchId, tableFilters, searchParams, setSearchParams]);

  const handleTabChange = useCallback((nextTab) => {
    setActiveTab(nextTab);
    setWorkflowId('');
    setBatchId('');
  }, []);

  const handleWorkflowChange = useCallback((newValue) => {
    setWorkflowId(newValue?.workflow_id ?? '');
    setBatchId('');
  }, []);

  const handleBatchChange = useCallback((newValue) => {
    setBatchId(newValue?.batch_id ?? '');
  }, []);

  const assignmentsQueryEnabled = Boolean(activeReqStatus && workflowId && batchId);
  const rows = assignmentsQueryEnabled ? (assignmentsPayload?.rows ?? []) : [];
  const rowCount = assignmentsQueryEnabled ? (assignmentsPayload?.totalCount ?? 0) : 0;

  const workflowValue = workflowOptions.find((w) => w.workflow_id === workflowId) ?? null;
  const batchValue = batchOptions.find((b) => b.batch_id === batchId) ?? null;
  const isAnnotation = (workflowValue?.workflow_type ?? 'SEGMENTATION') === 'ANNOTATION';

  const errorMessage =
    workflowsError?.message || batchesError?.message || assignmentsError?.message || '';
  const tableLoading =
    assignmentsQueryEnabled && (isAssignmentsLoading || isAssignmentsFetching);

  return {
    activeTab,
    workflowId,
    batchId,
    workflowValue,
    batchValue,
    workflowOptions,
    batchOptions,
    handleTabChange,
    handleWorkflowChange,
    handleBatchChange,
    isWorkflowLoading,
    isWorkflowFetching,
    isBatchesLoading,
    isBatchesFetching,
    isAnnotation,
    tagOptions,
    rows,
    rowCount,
    tableLoading,
    tableFilters,
    applyTableFiltersToUrl,
    pagination,
    setPagination,
    refetchAssignments,
    pageSizeOptions,
    defaultPageSize,
    errorMessage,
    sorting,
    onSortingChange: handleSortingChange,
  };
}
